import React, { useState, useEffect } from "react";
import arcBridge from "frontend/js/arcBridge";

const AccountCommunicationSettings = ({ settings, translations }) => {
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [preferences, setPreferences] = useState({
    latestEmail: false,
    latestSms: false,
    mjbEmail: false,
    mjbSms: false,
    storeEmail: false,
    storeSms: false,
    stopAllEmail: false,
    stopAllSms: false,
  });

  const handleCheckboxChange = (key) => async (e) => {
    const checked = e.target.checked;
    let updated = { ...preferences, [key]: checked };
  
    // If Stop All Email is toggled ON
    if (key === "stopAllEmail" && checked) {
      updated = {
        ...updated,
        latestEmail: false,
        mjbEmail: false,
        storeEmail: false,
      };
    }
  
    // If Stop All SMS is toggled ON
    if (key === "stopAllSms" && checked) {
      updated = {
        ...updated,
        latestSms: false,
        mjbSms: false,
        storeSms: false,
      };
    }
  
    // If an individual email option is toggled ON, disable Stop All Email
    if (["latestEmail", "mjbEmail", "storeEmail"].includes(key) && checked) {
      updated.stopAllEmail = false;
    }
  
    // If an individual SMS option is toggled ON, disable Stop All SMS
    if (["latestSms", "mjbSms", "storeSms"].includes(key) && checked) {
      updated.stopAllSms = false;
    }
  
    setPreferences(updated);
    await updatePreferences(updated);
  };

  const updatePreferences = async (prefs) => {
    const payload = {
      latest_comms: {
        email: prefs.latestEmail,
        sms: prefs.latestSms,
      },
      personalised_comms: {
        email: prefs.mjbEmail,
        sms: prefs.mjbSms,
      },
      store_comms: {
        email: prefs.storeEmail,
        sms: prefs.storeSms,
      },
      all_comms: {
        email: prefs.stopAllEmail,
        sms: prefs.stopAllSms,
      },
    };

    try {
      await arcBridge.updateCustomerPreferences(payload);
    } catch (error) {
      console.error("Error updating preferences:", error);
    }
  };

  const handleSavePreferences = async () => {
    setLoading(true);
    setSuccessMessage("");
    setErrorMessage("");
    
    try {
      await updatePreferences(preferences);
      setSuccessMessage(settings.section.preferences_success_message || "Preferences saved successfully!");
    } catch (error) {
        setErrorMessage(settings.section.preferences_error_message || "There was an error saving your preferences.");
    } finally {
        setLoading(false);
    }
  };

  const fetchPreferences = async () => {
    try {
      const response = await arcBridge.getCustomerPreferences();
      const data = response?.results;
  
      // If no preferences exist, assume it's a new user and set default values to true
      const isNewUser = !data || Object.keys(data).length === 0;
  
      setPreferences({
        latestEmail: data?.latest_comms?.email ?? isNewUser,
        latestSms: data?.latest_comms?.sms ?? isNewUser,
        mjbEmail: data?.personalised_comms?.email ?? isNewUser,
        mjbSms: data?.personalised_comms?.sms ?? isNewUser,
        storeEmail: data?.store_comms?.email ?? isNewUser,
        storeSms: data?.store_comms?.sms ?? isNewUser,
        stopAllEmail: data?.all_comms?.email ?? false,
        stopAllSms: data?.all_comms?.sms ?? false,
      });
    } catch (error) {
      setErrorMessage("Error fetching preferences.");
      console.error("Error fetching preferences:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPreferences();
  }, []);

  const renderCheckboxGroup = (idPrefix, emailKey, smsKey, label, desc) => (
    <div className="flex flex-col gap-y-4 lg:gap-y-[37px] lg:flex-row">
      <div className="w-full lg:max-w-[400px]">
        <p className="mb-2 text-black b1">{label}</p>
        <span className="b2 text-[#54565A]">{desc}</span>
      </div>
      <div className="flex gap-6">
        <div className="flex items-center gap-4">
          <label htmlFor={`${idPrefix}_email`} className="text-black b2">
            {translations.email}
          </label>
          <input
            type="checkbox"
            id={`${idPrefix}_email`}
            className="toggle-checkbox"
            checked={preferences[emailKey]}
            onChange={handleCheckboxChange(emailKey)}
          />
        </div>
        <div className="flex items-center gap-4">
          <label htmlFor={`${idPrefix}_sms`} className="text-black b2">
            {translations.sms}
          </label>
          <input
            type="checkbox"
            id={`${idPrefix}_sms`}
            className="toggle-checkbox"
            checked={preferences[smsKey]}
            onChange={handleCheckboxChange(smsKey)}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-10 account-communication">
      {renderCheckboxGroup(
        "latest",
        "latestEmail",
        "latestSms",
        translations.mjbale_communications,
        translations.mjbale_communications_details
      )}

      {renderCheckboxGroup(
        "mjb",
        "mjbEmail",
        "mjbSms",
        translations.mjb_members,
        translations.mjb_members_details
      )}

      <hr className="border-t border-[#ECEEEF]" />

      {renderCheckboxGroup(
        "store",
        "storeEmail",
        "storeSms",
        translations.store_communications,
        translations.store_communications_details
      )}

      <hr className="border-t border-[#ECEEEF]" />

      {renderCheckboxGroup(
        "stopall",
        "stopAllEmail",
        "stopAllSms",
        translations.stop_all,
        translations.stop_all_details
      )}

      <hr className="border-t border-[#ECEEEF]" />

      <div className="text-black b1">{translations.subtext}</div>

      <button
        className="w-full !text-[#00263E] button button-primary !bg-[#ECEBEB] lg:w-auto lg:!mt-[50px]"
        onClick={handleSavePreferences}
        disabled={loading}
      >
        {loading ? "Saving..." : translations.save_my_preference}
      </button>

      {errorMessage && <div className="!my-2.5 text-red-500 error-message">{errorMessage}</div>}
      {successMessage && <div className="!my-2.5 text-green-500 success-message">{successMessage}</div>}

      <div className="flex items-center !mt-6 b3 text-[#54565A] lg:!mt-8">
        <label htmlFor="privacy-policy">{translations.terms}</label>
      </div>
    </div>
  );
};

export default AccountCommunicationSettings;